import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import type { Role } from "../api";
import { LAYOUT_NAME } from "../nav";
import { useAuthActions, useSession } from "../session";
import { Card, EmptyState } from "./ui";

const RANK: Record<Role, number> = { starter: 0, pro: 1, admin: 2 };

/**
 * A route that needs a role. Like StrategyLab's ProToolGate, it never
 * redirects or 404s a shared link: the page explains what is behind it and
 * offers the one step that opens it — sign in, or switch layout.
 */
export function Guard({ role, children }: { role: Role; children: ReactNode }) {
  const { user, isLoading } = useSession();
  const { updateMe } = useAuthActions();
  const { pathname } = useLocation();
  if (isLoading) return null;

  if (!user) {
    return (
      <Card>
        <EmptyState
          icon="◇"
          title="Sign in to use this"
          action={
            <div className="row">
              <Link className="btn btn--primary" to={`/signin?next=${encodeURIComponent(pathname)}`}>
                Sign in
              </Link>
              <Link className="btn" to="/signup">
                Create an account
              </Link>
            </div>
          }
        >
          Everything public on StrategyHive is readable without an account. Building, running and sharing need
          one.
        </EmptyState>
      </Card>
    );
  }

  if (RANK[user.role] < RANK[role]) {
    return (
      <Card>
        <EmptyState
          icon="◆"
          title={`Part of the ${LAYOUT_NAME[role]} layout`}
          action={
            role === "pro" && user.canSwitchLayout ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => updateMe.mutate({ layout: "pro" })}
              >
                Turn on the Pro layout
              </button>
            ) : role === "pro" && user.isDemo ? (
              <Link className="btn btn--primary" to={`/signin?next=${encodeURIComponent(pathname)}`}>
                Sign in as Alex (Pro)
              </Link>
            ) : undefined
          }
        >
          {role === "admin"
            ? "This is the operator's console."
            : "It's off the Starter bar because it's most useful once you have a strategy you already believe in. You can switch back any time."}
        </EmptyState>
      </Card>
    );
  }

  return <>{children}</>;
}
